import { createFileRoute } from '@tanstack/react-router'
import { LegalPage } from '@/components/landing/legal-page'

export const Route = createFileRoute('/privacy')({
  component: Privacy,
  head: () => ({
    meta: [
      { title: 'Privacy Policy | Vello' },
      {
        name: 'description',
        content:
          'What Vello collects, how it is used, and the vendors data is shared with.',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://withvello.com/privacy' }],
  }),
})

function Privacy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      updated="April 19, 2026"
    >
      <p>
        This page explains what Vello collects when you visit withvello.com or
        buy a plan, why it is collected, who it is shared with, and how you can
        ask for it to be removed. Vello collects the minimum needed to run the
        business.
      </p>

      <h2>What Vello collects</h2>
      <p>
        <strong>When you visit the site.</strong> Google Analytics 4 records
        anonymous events like page views, button clicks, device type, approximate
        location (country / city), and referrer. Cookies are used to distinguish
        visitors. This data is not used to build advertising profiles.
      </p>
      <p>
        <strong>When you buy.</strong> Stripe Checkout collects your name,
        email, billing address, and payment method to process the payment. Card
        numbers never touch Vello servers. Vello only receives a confirmation,
        an order ID, and the email you used.
      </p>
      <p>
        <strong>When you submit an intake or message.</strong> The content you
        submit (company, logo, brand references, the copy you want on the site,
        CRM destination) is kept to deliver the project, along with any emails
        you send in.
      </p>

      <h2>How Vello uses it</h2>
      <ul>
        <li>To deliver and support the site being built for you.</li>
        <li>To process payments and send invoices or receipts.</li>
        <li>
          To understand which pages and CTAs are working, in aggregate, and
          improve the landing page.
        </li>
        <li>To reply to your emails.</li>
      </ul>
      <p>
        Your data is never sold. No marketing emails are sent without your
        opt-in.
      </p>

      <h2>Who Vello shares it with</h2>
      <ul>
        <li>
          <strong>Stripe</strong>: payment processing.{' '}
          <a
            href="https://stripe.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Stripe&apos;s privacy policy
          </a>
          .
        </li>
        <li>
          <strong>Google Analytics 4</strong>: anonymous site usage.{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Google&apos;s privacy policy
          </a>
          .
        </li>
        <li>
          <strong>Cloudflare</strong>: hosting, edge routing, and DDoS
          protection for withvello.com.
        </li>
        <li>
          <strong>Your chosen CRM</strong>: if you request leads wired to
          HubSpot, Pipedrive, Attio, Close, Folk, or a webhook, your form
          submissions go there.
        </li>
      </ul>

      <h2>Cookies</h2>
      <p>
        Vello uses a small number of first-party cookies plus Google Analytics
        cookies. No advertising or cross-site tracking cookies. You can block or
        delete them in your browser settings without breaking the site.
      </p>

      <h2>Retention</h2>
      <p>
        Analytics events are retained for the Google Analytics default (14
        months). Stripe retains payment records per its own policy. Intake and
        project files are retained for as long as you are an active Vello+
        customer, plus 12 months after cancellation for support and records.
        Email threads are retained until you request deletion.
      </p>

      <h2>Your rights</h2>
      <p>
        You can request at any time to: see what data is held on you, correct
        it, export it, or delete it. Email{' '}
        <a href="mailto:hello@withvello.com">hello@withvello.com</a> and the
        request is actioned within 30 days. If you are in the EU, UK, or
        California, you have additional rights under GDPR / UK GDPR / CCPA,
        including the right to object to processing and the right to lodge a
        complaint with your local data protection authority.
      </p>

      <h2>Children</h2>
      <p>
        Vello is not intended for people under 16. Data from children is not
        knowingly collected.
      </p>

      <h2>Changes</h2>
      <p>
        If this policy is updated, the &ldquo;Last updated&rdquo; date at the
        top will change. Material changes will be announced to active customers
        by email.
      </p>

      <h2>Contact</h2>
      <p>
        Privacy questions, deletion requests, or anything else:{' '}
        <a href="mailto:hello@withvello.com">hello@withvello.com</a>.
      </p>
    </LegalPage>
  )
}
